import React, { useRef, useState } from 'react';

import { Accordion as StrapAccordion } from 'reactstrap';
import { css, styled } from 'styled-components';

import theme from 'styles/theme';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import Flex from 'components/Flex/Flex';
import Accordion from 'components/ModularBlocks/AccordionBlock/components/Accordion/Accordion';

import { AccordionBlockI, AccordionContentI } from 'interfaces/content-stack/modular-blocks/accordion';

import { autoOptimizeImage } from 'utils/functions';

const MainText = styled('h2')(
  ({ theme }) => css`
    font-size: 2rem;
    color: ${theme.colors.primaryBlack2};
    font-family: ${theme.fonts.fontFamilySecondary};
    margin: 0;
    font-weight: 500;
    line-height: 1.6;
  `
);

const AccordionContainer = styled('div')<{ $isWorksansHeaderFont: boolean }>(
  ({ theme, $isWorksansHeaderFont }) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 50%;

    @media (max-width: ${theme.breakpoints.lg}) {
      width: 100%;
    }

    .accordion-item {
      border: none;
      margin-bottom: 2rem;
    }

    .accordion-header {
      border-bottom: 0.0625rem solid ${theme.colors.primaryRed};

      .accordion-button {
        padding: 0 0 0.5rem 0;
        font-family: ${$isWorksansHeaderFont ? theme.fonts.default : theme.fonts.fontFamilySecondary};
        font-weight: ${$isWorksansHeaderFont ? 700 : 500};
        text-transform: ${$isWorksansHeaderFont ? 'none' : 'uppercase'};
        font-size: 1.25rem;

        &:not(.collapsed) {
          background-color: transparent;
          color: ${theme.colors.primaryBlack2};

          &:after {
            transform: rotate(-135deg);
          }
        }

        &:focus {
          border: none;
          box-shadow: none;
        }

        &:after {
          font-family: 'Font Awesome 5 Free';
          font-weight: 900;
          font-size: 1.25rem;
          content: '\\2b';
          color: ${theme.colors.primaryBlack2};
          float: right;
          position: relative;
          display: block;
          text-align: center;
          transition: 0.3s all;
          transform: rotate(0deg);
          transform-origin: 10px 12px;
          opacity: 100;
          background-image: none;
        }
      }
    }

    .accordion-body {
      padding: 0;
    }
  `
);

const Img = styled('img')(
  () => css`
    width: 100%;
  `
);

export interface AccordionBlockProps {
  data: AccordionBlockI;
}

const AccordionBlock: React.FC<AccordionBlockProps> = ({ data }) => {
  const { header, blockImage, accordion } = data;
  const { firstItemOpen, itemHeaderFontFamily, accordionItems } = accordion;
  const isWorksansHeaderFont = itemHeaderFontFamily === 'Worksans Variablefont Wght';
  const [open, setOpen] = useState<string>(firstItemOpen ? '1' : '');
  const accordionBlockRef = useRef<HTMLDivElement | null>(null);
  const isMobileDevice = () => window.matchMedia(`(max-width: ${theme.breakpoints.md})`).matches;

  const toggle = (id: string) => {
    if (open === id) {
      setOpen('');
    } else {
      setOpen(id);
      if (isMobileDevice()) {
        accordionBlockRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  return (
    <ContentContainer>
      <Flex w100 responsiveWrap styles={{ minHeight: '7rem' }}>
        <Flex responsive column gap={3}>
          <MainText>{header}</MainText>
          <Img src={autoOptimizeImage(blockImage?.image?.url)} alt={blockImage?.imageAltText ?? 'Image'} />
        </Flex>
        <AccordionContainer ref={accordionBlockRef} $isWorksansHeaderFont={isWorksansHeaderFont}>
          <StrapAccordion flush open={open} toggle={toggle}>
            {accordionItems?.map((accordionItem: AccordionContentI, i: number) => (
              <Accordion accordionItem={accordionItem} key={i} id={`${i + 1}`} />
            ))}
          </StrapAccordion>
        </AccordionContainer>
      </Flex>
    </ContentContainer>
  );
};

export default AccordionBlock;
